import { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  ADD_FAVORITE_MUTATION,
  REMOVE_FAVORITE_MUTATION,
  FAVORITES_QUERY,
  CUSTOMER_ARTICLES_QUERY,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { FavoriteIcon } from './favorite-icon';
import { useNavHighlight } from './nav-highlight-provider';

export interface FavoriteToggleButtonProps {
  articleNumber: string;
  customerNumber?: string;
  isFavorite: boolean;
  disabled?: boolean;
  disabledTitle?: string;
  className?: string;
}

export function FavoriteToggleButton({
  articleNumber,
  customerNumber,
  isFavorite,
  disabled = false,
  disabledTitle,
  className,
}: FavoriteToggleButtonProps) {
  const [displayFavorite, setDisplayFavorite] = useState(isFavorite);
  const { markHighlight } = useNavHighlight();

  useEffect(() => {
    setDisplayFavorite(isFavorite);
  }, [isFavorite]);

  const mutation = displayFavorite ? REMOVE_FAVORITE_MUTATION : ADD_FAVORITE_MUTATION;
  const [toggle, { loading }] = useMutation(mutation, {
    variables: { articleNumber, customerNumber },
    refetchQueries: [FAVORITES_QUERY, CUSTOMER_ARTICLES_QUERY],
  });

  const handleClick = () => {
    const previous = displayFavorite;
    const next = !previous;
    setDisplayFavorite(next);
    toggle({
      onCompleted: () => {
        if (!previous) {
          markHighlight('favoriten');
        }
      },
      onError: () => {
        setDisplayFavorite(previous);
        toast.error(
          previous
            ? 'Favorit konnte nicht entfernt werden.'
            : 'Favorit konnte nicht gespeichert werden.'
        );
      },
    });
  };

  return (
    <button
      type="button"
      aria-pressed={displayFavorite}
      aria-label={displayFavorite ? 'Favorit entfernen' : 'Als Favorit markieren'}
      aria-busy={loading || undefined}
      disabled={loading || disabled}
      title={disabled ? disabledTitle : 'Favorit'}
      onClick={handleClick}
      className={clsx(
        'p-2.5 rounded transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        displayFavorite
          ? 'text-primary bg-orange-50 hover:bg-orange-100'
          : 'text-gray-500 hover:text-primary hover:bg-orange-50',
        className
      )}
    >
      <FavoriteIcon size={18} fill={displayFavorite ? 'currentColor' : 'none'} />
    </button>
  );
}
